import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IncotermService } from '../../../../services/forwarding/incoterm.service';
import { Incoterm, CreateIncotermDto, UpdateIncotermDto } from '../../../../models/forwarding/incoterm.model';

@Component({
  selector: 'app-incoterms-management-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './incoterms-management-modal.html',
  styleUrl: './incoterms-management-modal.css'
})
export class IncotermsManagementModalComponent implements OnInit, OnDestroy {
  showModal = false;
  showAddModal = false;
  showEditModal = false;
  incoterms: Incoterm[] = [];
  filteredIncoterms: Incoterm[] = [];
  searchTerm = '';
  private subscriptions = new Subscription();

  formData: CreateIncotermDto = {
    code: '',
    description: ''
  };

  editFormData: UpdateIncotermDto = {
    code: '',
    description: '',
    isActive: true
  };

  currentEditId: number | null = null;

  constructor(private incotermService: IncotermService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.incotermService.getIncoterms().subscribe(incoterms => {
        this.incoterms = incoterms;
        this.filteredIncoterms = incoterms;
        console.log('[IncotermsManagementModal] Incoterms updated:', incoterms.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(): void {
    this.showModal = true;
    console.log('[IncotermsManagementModal] Modal opened');
  }

  close(): void {
    this.showModal = false;
    console.log('[IncotermsManagementModal] Modal closed');
  }

  openAddIncotermModal(): void {
    this.showAddModal = true;
    this.resetForm();
    console.log('[IncotermsManagementModal] Add modal opened');
  }

  closeAddIncotermModal(): void {
    this.showAddModal = false;
    this.resetForm();
  }

  openEditIncotermModal(incoterm: Incoterm): void {
    this.showEditModal = true;
    this.currentEditId = incoterm.id;
    this.editFormData = {
      code: incoterm.code,
      description: incoterm.description,
      isActive: incoterm.isActive
    };
    console.log('[IncotermsManagementModal] Edit modal opened for incoterm:', incoterm.id);
  }

  closeEditIncotermModal(): void {
    this.showEditModal = false;
    this.currentEditId = null;
    this.editFormData = { code: '', description: '', isActive: true };
  }

  filterIncoterms(): void {
    const search = this.searchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredIncoterms = this.incoterms;
      return;
    }

    this.filteredIncoterms = this.incoterms.filter(i =>
      i.code.toLowerCase().includes(search) ||
      i.description.toLowerCase().includes(search)
    );
  }

  // Auto-uppercase the code as user types
  onCodeInput(): void {
    this.formData.code = this.formData.code.toUpperCase();
  }

  onEditCodeInput(): void {
    this.editFormData.code = this.editFormData.code.toUpperCase();
  }

  createIncoterm(): void {
    if (!this.formData.code || !this.formData.description) {
      alert('Please enter both incoterm code and description');
      return;
    }

    if (this.formData.code.length > 10) {
      alert('Incoterm code must be 10 characters or less');
      return;
    }

    // Ensure code is uppercase before sending
    this.formData.code = this.formData.code.toUpperCase();

    this.incotermService.create(this.formData).subscribe({
      next: (incoterm) => {
        console.log('[IncotermsManagementModal] Incoterm created successfully:', incoterm);
        this.closeAddIncotermModal();
      },
      error: (error) => {
        console.error('[IncotermsManagementModal] Error creating incoterm:', error);
        alert('Error creating incoterm. Please try again.');
      }
    });
  }

  updateIncoterm(): void {
    if (!this.currentEditId || !this.editFormData.code || !this.editFormData.description) {
      alert('Please enter both incoterm code and description');
      return;
    }

    if (this.editFormData.code.length > 10) {
      alert('Incoterm code must be 10 characters or less');
      return;
    }

    // Ensure code is uppercase before sending
    this.editFormData.code = this.editFormData.code.toUpperCase();

    this.incotermService.update(this.currentEditId, this.editFormData).subscribe({
      next: (incoterm) => {
        console.log('[IncotermsManagementModal] Incoterm updated successfully:', incoterm);
        this.closeEditIncotermModal();
      },
      error: (error) => {
        console.error('[IncotermsManagementModal] Error updating incoterm:', error);
        alert('Error updating incoterm. Please try again.');
      }
    });
  }

  deleteIncoterm(id: number): void {
    if (!confirm('Are you sure you want to delete this incoterm?')) {
      return;
    }

    this.incotermService.delete(id).subscribe({
      next: () => {
        console.log('[IncotermsManagementModal] Incoterm deleted successfully');
      },
      error: (error) => {
        console.error('[IncotermsManagementModal] Error deleting incoterm:', error);
        alert('Error deleting incoterm. Please try again.');
      }
    });
  }

  private resetForm(): void {
    this.formData = {
      code: '',
      description: ''
    };
  }
}
